/**
 * contactStore.ts
 *
 * Pinia store that manages the contact sync lifecycle on the web side.
 *
 * State machine:
 *   unpaired  → User has no active session in IndexedDB (first visit or key loss).
 *   paired    → Session is restored and the RTDB listener is active.
 *   syncing   → Waiting for the first payload or a manual sync to complete.
 *   error     → Decryption or RTDB error occurred.
 *
 * Responsibilities:
 *   - On [initialize]: restore session from IndexedDB and wire up RTDB listener.
 *   - On [pair]: generate keypair + session, persist to IndexedDB, and start listening.
 *   - On [requestSync]: set syncRequestedAt in RTDB to trigger mobile push.
 *   - On [unlink]: delete RTDB channel, wipe IndexedDB, reset state.
 */

import { defineStore } from 'pinia';
import { readSession, writeSession, clearAll, readCachedContacts, clearCachedContacts } from '~/services/contactSyncStorage';
import { generateWebKeyPair } from '~/services/contactSyncCrypto';
import { watchSyncChannel, requestManualSync, deleteChannel } from '~/services/contactSync';
import { normalizePhoneNumber, phoneSuffixKey, commonSuffixLength } from '~/utils/phone';
import type { Database } from 'firebase/database';
import type { SyncedContact } from '~/types/contacts';

// ── Types ─────────────────────────────────────────────────────────────────────

// Re-export so consumers can import SyncedContact from either location.
export type { SyncedContact } from '~/types/contacts';

type SyncStatus = 'unpaired' | 'paired' | 'syncing' | 'error';

// ── Store ─────────────────────────────────────────────────────────────────────

export const useContactStore = defineStore('contactStore', () => {
  const contacts = ref<SyncedContact[]>([]);
  const status = ref<SyncStatus>('unpaired');
  const deviceId = ref<string | null>(null);
  const publicKeyBase64 = ref<string | null>(null); // for QR code display
  const linkedAt = ref<string | null>(null);
  const lastSyncedAt = ref<number | null>(null);
  const errorMessage = ref<string | null>(null);

  // Unsubscribe function returned by watchSyncChannel.
  let _unsubscribe: (() => void) | null = null;

  // ── Initialization (call on protected page load) ──────────────────────────

  /**
   * Restores an existing session from IndexedDB and starts the RTDB listener.
   * If no session exists, sets status to 'unpaired' so the UI can show the QR.
   *
   * Must be called with the authenticated Firebase UID and the RTDB instance.
   */
  async function initialize(uid: string, db: Database): Promise<void> {
    try {
      const session = await readSession();

      if (!session?.deviceId || !session?.privateKey) {
        // No valid session — show the pairing QR.
        status.value = 'unpaired';
        return;
      }

      // Detect a different user logging in on the same browser. If the session
      // was left by a previous user, wipe everything (contacts + session) so
      // their data isn't leaked and also so this user gets a clean pairing flow.
      if (session.uid && session.uid !== uid) {
        await clearAll();
        status.value = 'unpaired';
        return;
      }

      // Session found. Check if pairing was ever confirmed (mobile scanned the QR
      // and pushed at least one payload). A null linkedAt means the session was
      // written by preparePairing() but the QR was never scanned — the page was
      // likely refreshed while showing the QR. Treat it as unpaired so the UI
      // re-renders the QR rather than getting stuck at "Syncing contacts…".
      if (!session.linkedAt) {
        status.value = 'unpaired';
        return;
      }

      // Confirmed session: restore state and start listening.
      deviceId.value = session.deviceId;
      publicKeyBase64.value = session.publicKeyBase64;
      linkedAt.value = session.linkedAt;
      status.value = 'syncing';

      // Seed the UI with the last-known contacts while waiting for an update.
      const cached = await readCachedContacts();
      if (cached.length > 0) {
        contacts.value = cached;
        status.value = 'paired';
      }

      _startListening(uid, db, session.deviceId, session.privateKey);
    } catch (err) {
      console.error('[contactStore] initialize error', err);
      status.value = 'error';
      errorMessage.value = 'Failed to restore contact sync session.';
    }
  }

  /**
   * Generates a new keypair and session for QR-code display, then immediately
   * starts the RTDB listener so the page transitions to 'syncing' the moment
   * the mobile app scans and pushes the first payload.
   *
   * Returns a ready-to-encode QR code string.
   */
  async function preparePairing(uid: string, db: Database, label?: string): Promise<string> {
    const keyPair = await generateWebKeyPair();
    const id = crypto.randomUUID();
    const browserLabel = label ?? _detectBrowserLabel();

    const session = {
      deviceId: id,
      privateKey: keyPair.privateKey,
      publicKeyBase64: keyPair.publicKeyBase64,
      // linkedAt stays null until the mobile scans and pushes the first
      // payload. This lets initialize() distinguish a pending QR session from
      // a confirmed paired device, preventing the spurious "Syncing…" state
      // when the user refreshes the page before ever scanning.
      linkedAt: null as string | null,
      uid,
    };

    await writeSession(session);

    deviceId.value = id;
    publicKeyBase64.value = keyPair.publicKeyBase64;
    linkedAt.value = session.linkedAt;
    status.value = 'unpaired'; // Still unpaired until mobile scans.

    // Start listening now so the first payload from the mobile app is caught
    // in real-time rather than requiring a page reload.
    _startListening(uid, db, id, keyPair.privateKey);

    return JSON.stringify({
      deviceId: id,
      publicKey: keyPair.publicKeyBase64,
      label: browserLabel,
    });
  }

  /**
   * Requests an on-demand sync. The mobile app's RTDB listener detects the
   * updated timestamp and pushes a fresh encrypted payload.
   */
  async function requestSync(uid: string, db: Database): Promise<void> {
    if (!deviceId.value) return;
    await requestManualSync(db, uid, deviceId.value);
    status.value = 'syncing';
  }

  /**
   * Resolves a phone number to a contact name using the same loose matching
   * strategy as the mobile app.
   */
  function getNameForNumber(number: string): string | null {
    const normalized = normalizePhoneNumber(number);
    if (!normalized) return null;

    // Fast-path: check direct match on normalized number
    for (const contact of contacts.value) {
      if (contact.phones.some(p => p.normalizedNumber === normalized)) {
        return contact.displayName;
      }
    }

    // Fallback: suffix-based loose match
    const suffix = phoneSuffixKey(normalized);
    if (!suffix) return null;

    // Collect candidates that share the same suffix
    const candidates: { contact: SyncedContact; normLen: number }[] = [];
    for (const contact of contacts.value) {
      for (const p of contact.phones) {
        if (phoneSuffixKey(p.normalizedNumber) === suffix) {
          candidates.push({ contact, normLen: p.normalizedNumber.length });
        }
      }
    }

    if (candidates.length === 0) return null;
    if (candidates.length === 1) return candidates[0].contact.displayName;

    // Pick the best candidate based on common suffix length tie-broken by shortest normalized length
    let bestContact: SyncedContact | null = null;
    let bestScore = -1;
    let bestNormLen = 99999;

    for (const { contact, normLen } of candidates) {
      // Find the phone in contact that actually matched
      for (const p of contact.phones) {
        if (phoneSuffixKey(p.normalizedNumber) === suffix) {
          const score = commonSuffixLength(normalized, p.normalizedNumber);
          if (score > bestScore) {
            bestScore = score;
            bestContact = contact;
            bestNormLen = p.normalizedNumber.length;
          } else if (score === bestScore && bestContact) {
            if (p.normalizedNumber.length < bestNormLen) {
              bestContact = contact;
              bestNormLen = p.normalizedNumber.length;
            }
          }
        }
      }
    }

    return bestContact?.displayName || null;
  }

  /**
   * Unlinks this web session: deletes the RTDB channel, clears IndexedDB,
   * and resets all state. The user must scan a new QR code to re-pair.
   */
  /**
   * Clears the decrypted contacts cache and resets in-memory state on logout,
   * but intentionally keeps the session keypair in IndexedDB so the same user
   * can log back in without re-pairing. A different user logging in on the same
   * browser will be detected by the uid mismatch check in initialize().
   *
   * The RTDB listener is stopped and the channel is NOT deleted — the mobile
   * pairing remains intact for the next login.
   */
  async function clearForLogout(): Promise<void> {
    if (_unsubscribe) {
      _unsubscribe();
      _unsubscribe = null;
    }
    await clearCachedContacts();
    _reset();
  }

  async function unlink(uid: string, db: Database): Promise<void> {
    if (_unsubscribe) {
      _unsubscribe();
      _unsubscribe = null;
    }

    if (deviceId.value) {
      await deleteChannel(db, uid, deviceId.value).catch(() => {
        // Best-effort — local cleanup proceeds regardless.
      });
    }

    await clearAll();
    _reset();
  }

  // ── Internal ──────────────────────────────────────────────────────────────

  function _startListening(
    uid: string,
    db: Database,
    id: string,
    privateKey: CryptoKey,
  ): void {
    if (_unsubscribe) _unsubscribe();

    _unsubscribe = watchSyncChannel(
      db,
      uid,
      id,
      privateKey,
      (updated) => {
        contacts.value = updated;
        lastSyncedAt.value = Date.now();

        // First successful payload from the mobile confirms the pairing.
        // Persist the linkedAt timestamp so initialize() treats this session
        // as a confirmed pair on future page loads.
        if (!linkedAt.value) {
          linkedAt.value = new Date().toISOString();
          // Also persists uid so initialize() can detect cross-user sessions.
          readSession().then((existing) => {
            writeSession({
              deviceId: id,
              privateKey,
              publicKeyBase64: publicKeyBase64.value ?? '',
              linkedAt: linkedAt.value!,
              uid: existing?.uid ?? '',
            });
          }).catch((err) => console.error('[contactStore] failed to persist linkedAt', err));
        }

        status.value = 'paired';
        errorMessage.value = null;
      },
      (err) => {
        status.value = 'error';
        errorMessage.value =
          err instanceof Error ? err.message : 'Contact sync error.';
      },
      // Mobile app deleted the channel (unlink/revocation) — clear local state.
      async () => {
        if (_unsubscribe) {
          _unsubscribe();
          _unsubscribe = null;
        }
        await clearAll();
        _reset();
      },
    );
  }

  function _reset(): void {
    contacts.value = [];
    status.value = 'unpaired';
    deviceId.value = null;
    publicKeyBase64.value = null;
    linkedAt.value = null;
    lastSyncedAt.value = null;
    errorMessage.value = null;
  }

  return {
    // State — returned as plain refs so Pinia's SSR hydration can restore
    // server state on the client. Components should use actions to mutate state.
    contacts,
    status,
    deviceId,
    publicKeyBase64,
    linkedAt,
    lastSyncedAt,
    errorMessage,

    // Actions
    initialize,
    preparePairing,
    requestSync,
    clearForLogout,
    unlink,
    getNameForNumber,
    startListening: _startListening,
  };
});

// ── Helpers ───────────────────────────────────────────────────────────────────

function _detectBrowserLabel(): string {
  const ua = navigator.userAgent;
  const os = /Mac/.test(ua)
    ? 'macOS'
    : /Win/.test(ua)
      ? 'Windows'
      : /Linux/.test(ua)
        ? 'Linux'
        : /iPhone|iPad/.test(ua)
          ? 'iOS'
          : /Android/.test(ua)
            ? 'Android'
            : 'Unknown OS';

  const browser = /Edg\//.test(ua)
    ? 'Edge'
    : /OPR\//.test(ua)
      ? 'Opera'
      : /Firefox\//.test(ua)
        ? 'Firefox'
        : /Chrome\//.test(ua)
          ? 'Chrome'
          : /Safari\//.test(ua)
            ? 'Safari'
            : 'Browser';

  return `${browser} on ${os}`;
}
